import React, { useContext } from 'react'
import copy from 'copy-to-clipboard'
import { useHistory, useParams } from 'react-router'
import qs from 'qs'

import { Dropdown, useDropdownState } from '@sketch/components'
import { useToast } from '@sketch/toasts'
import { LaunchSketchAlert } from 'containers/modals/LaunchSketchAlert'
import { ModalContext } from 'containers/ModalManager'
import {
  RouteParams,
  QueryParams,
  versionedRoutes,
  IS_EMBEDDED,
} from '@sketch/constants'
import { useVersioning } from 'modules/versioning'

import { StyledCanvasContextMenu } from './CanvasContextMenu.styles'
import { ContextMenuMetaData, Coordinates } from './useCanvasContextMenu'
import { INSPECTOR_SEGMENT } from 'modules/shares/constants'

type CanvasContextMenuProps = {
  contextMenuPos: Coordinates | null
  contextMenuMetaData: ContextMenuMetaData | null
  closeTooltip: () => void
}

/**
 * Menu displayed when right clicking on the canvas.
 */
function CanvasContextMenu({
  contextMenuPos,
  contextMenuMetaData,
  closeTooltip,
}: CanvasContextMenuProps) {
  const routeParams = useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()
  const { versionShortId, getPathname, share } = useVersioning()
  const { showToast } = useToast()
  const { showModal } = useContext(ModalContext)
  const history = useHistory()

  const dropdown = useDropdownState({
    onToggle: closeTooltip,
  })

  if (contextMenuPos === null || !contextMenuMetaData) {
    return null
  }

  function handleCopyLinkToCoordinates() {
    // We want to always use a versioned route (rather than implicitly linking
    // to the latest version) so that URLs are not affected when a new version
    // is created.
    let currentPathname
    if (routeParams.pageUUID && routeParams.pageUUID !== '') {
      // Only include a pageUUID if the user has navigated away from the first page
      currentPathname = versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION.create({
        pageUUID: routeParams.pageUUID,
        shareID: routeParams.shareID,
        versionShortId,
      })
    } else {
      currentPathname = versionedRoutes.SHARE_VIEW.VERSION.create({
        shareID: routeParams.shareID,
        versionShortId,
      })
    }

    const queryParams: QueryParams<'SHARE_PAGE_CANVAS_VIEW'> = {
      posX: contextMenuMetaData!.canvasPanPosition.posX.toString(),
      posY: contextMenuMetaData!.canvasPanPosition.posY.toString(),
      zoom: contextMenuMetaData!.zoom.toString(),
    }

    const link = `${window.location.origin}${currentPathname}?${qs.stringify(
      queryParams
    )}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleCopyLinkToArtboard() {
    if (!contextMenuMetaData?.artboardUUID) {
      return
    }
    const currentPathname = getPathname({
      routeKey: 'ARTBOARD_DETAIL_UUID',
      routeParams: {
        shareID: routeParams.shareID,
        artboardUUID: contextMenuMetaData.artboardUUID,
      },
    })

    const link = `${window.location.origin}${currentPathname}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleEdit() {
    closeTooltip()
    showModal(LaunchSketchAlert, {
      shareId: routeParams.shareID,
      pageUUID: routeParams.pageUUID,
      action: 'openInSketch',
    })
  }

  function handleInspectArtboard() {
    if (!contextMenuMetaData?.artboardUUID) {
      return
    }

    const currentPathname = getPathname({
      routeKey: 'ARTBOARD_DETAIL_UUID',
      routeParams: {
        shareID: routeParams.shareID,
        artboardUUID: contextMenuMetaData.artboardUUID,
      },
    })

    closeTooltip()
    history.push(`${currentPathname}#${INSPECTOR_SEGMENT}`)
  }

  const hasArtboardUUID = contextMenuMetaData.artboardUUID !== null

  const dropdownTitle = hasArtboardUUID ? 'Artboard' : 'Canvas'
  const showInspectArtboard =
    share.userCanInspect && hasArtboardUUID && !IS_EMBEDDED

  return (
    <StyledCanvasContextMenu
      {...dropdown}
      placement="bottom-start"
      spacing="20px"
      style={{
        left: contextMenuPos.posX,
        top: contextMenuPos.posY,
      }}
      visible={true}
      onClickOutside={closeTooltip}
    >
      <Dropdown.Header>{dropdownTitle}</Dropdown.Header>
      {contextMenuMetaData.artboardUUID !== null && (
        <Dropdown.Item onClick={handleCopyLinkToArtboard}>
          Copy Link
        </Dropdown.Item>
      )}
      <Dropdown.Item onClick={handleCopyLinkToCoordinates}>
        Copy Link from Position
      </Dropdown.Item>
      {showInspectArtboard && (
        <Dropdown.Item onClick={handleInspectArtboard}>
          Inspect Artboard
        </Dropdown.Item>
      )}
      {share.userCanEdit && (
        <Dropdown.Item onClick={handleEdit}>Edit</Dropdown.Item>
      )}
    </StyledCanvasContextMenu>
  )
}

export default CanvasContextMenu
